import queryString from 'query-string';
import { RouteObject } from 'react-router';

import { configString } from 'config/appConfigUtils';
import { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { salariesApiFetchJobSynonym } from 'modules/salaries/api/salariesApiFetchJobSynonym';
import { searchAlgoliaFetchFacetCounts } from 'modules/search/algolia/fetch/searchAlgoliaFetchFacetCounts';
import { SEARCH_JOBS_FILTERS } from 'modules/search/constants/filters/searchJobsFilters';
import { SearchJobsPageNewOrLegacy } from 'modules/search/pages/Jobs/SearchJobsPageNewOrLegacy';
import { SearchJobsPageFilters } from 'modules/search/pages/Jobs/components/SearchJobsPageFilters';
import { AppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { renderLoader } from 'routing/utils/renderLoader/renderLoader';

import { searchGetJobsRoutePath } from './helpers/searchGetJobsRoutePath';
import { searchJobsRouteReference } from './search.routes';

const urlByLocale = {
  en: searchGetJobsRoutePath({ lang: 'en' }),
  es: searchGetJobsRoutePath({ lang: 'es' }),
  pt: searchGetJobsRoutePath({ lang: 'pt' }),
} as const satisfies Record<UserLocale, string>;

// @NOTE - This is instantiated outside of the loader scope because we need
// the current _and_ next params from `shouldRevalidate` to get
// this value that is then needed in the loader
// This is totally a hack and we should find a better way to do it at some
// point.
let updatedFilter: string | undefined;

export const searchJobsRouteObjects = (['en', 'es', 'pt'] as const).map(
  (locale) => ({
    path: urlByLocale[locale],
    id: `search-jobs-${locale}`,
    handle: {
      searchFilters: <SearchJobsPageFilters />,
      urlByLocale,
      defaultSearchType: 'JOB',
      hideFooterDivider: true,
      showDesktopSearch: true,
      isSearchPage: true,
    } satisfies LayoutHandleProps,
    ...renderLoader({
      loader: async ({
        request,
      }: AppRouteLoaderArgs<typeof searchJobsRouteReference>) => {
        try {
          const url = new URL(request.url);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { q, ...filterParams } = queryString.parse(
            url.searchParams.toString(),
          );

          const activeFilters = {
            ...filterParams,
            type: 'JOB',
          };

          // Here we are updating all of the facet counts _except_
          // the one that was just updated, because we are using
          // `OR` filters and as such, the counts shouldn't change
          const [facetCounts] = await Promise.all([
            searchAlgoliaFetchFacetCounts({
              filters: activeFilters,
              facets: SEARCH_JOBS_FILTERS.filter(
                (filterType) => filterType !== updatedFilter,
              ),
              indexName: configString('algolia', 'siteIndexName'),
            }),
          ]);

          const synonymId = url.searchParams.get('synonymId');

          const [synonym] = await Promise.all([
            synonymId
              ? salariesApiFetchJobSynonym(synonymId)
              : Promise.resolve(),
          ]);

          return { facetCounts, synonym };
        } catch (e) {
          // @TODO: Should we log and/or throw an error?
          return { synonym: null, facetCounts: null };
        }
      },
      render: () => <SearchJobsPageNewOrLegacy />,
    }),
    shouldRevalidate: ({ currentUrl, defaultShouldRevalidate, nextUrl }) => {
      if (currentUrl.searchParams.size === nextUrl.searchParams.size) {
        return false;
      }

      updatedFilter = [...nextUrl.searchParams.keys()].find((key) => {
        const currentValues = currentUrl.searchParams.getAll(key);
        const nextValues = nextUrl.searchParams.getAll(key);

        return (
          !currentUrl.searchParams.has(key) ||
          currentValues.length !== nextValues.length
        );
      });

      return defaultShouldRevalidate;
    },
  }),
) satisfies RouteObject[];
